import { Box, Typography } from '@mui/material';
import { type StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import { filterEntitiesByState } from '@repo/ui/utils/geo-data';
import React from 'react';
import ProductCardWrapper from './ProductCardsWrapper';
import { ProductCardCategorySectionsSkeleton } from './Skeleton';

const getBadgeText = (
  sectionEyebrowText?: string,
  sectionTitle?: string,
): string | undefined => {
  if (sectionEyebrowText && sectionTitle) {
    return `${sectionEyebrowText} ${sectionTitle}`;
  }
};

interface Props {
  title?: string;
  description?: string;
  eyebrowText?: string;
  productCards: ProductCardType[];
  productPositionBase: number;
  isSeoBankingFFActive: boolean;
  regionCode?: StateAbbreviation;
  showSkeleton?: boolean;
  shouldApplyStylingUpdates?: boolean;
  isImproveQualityTrafficExperimentActive?: boolean;
  shouldDisplayWealthFrontModal?: boolean;
}

export default function CategorySection({
  title,
  description,
  eyebrowText,
  productCards,
  productPositionBase,
  isSeoBankingFFActive,
  regionCode,
  showSkeleton = false,
  shouldApplyStylingUpdates = false,
  isImproveQualityTrafficExperimentActive = false,
  shouldDisplayWealthFrontModal = false,
}: Props): React.JSX.Element | null {
  const geoTargetedCards = regionCode
    ? filterEntitiesByState<ProductCardType>(productCards, regionCode)
    : productCards;

  if (!geoTargetedCards.length) {
    return null;
  }

  if (showSkeleton) {
    return (
      <ProductCardCategorySectionsSkeleton
        showTitleSkeleton={!!title || !!eyebrowText}
        cardsAmount={geoTargetedCards.length}
        isSeoBankingFFActive={isSeoBankingFFActive}
      />
    );
  }

  const cardBadgeText = getBadgeText(eyebrowText, title);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        maxWidth: { xs: '100%', md: '82%' },
        marginX: 'auto',
        marginBottom: 8,
      }}
      data-testid="product-card-section"
      component="li"
    >
      {(title || eyebrowText) && (
        <Box sx={{ marginBottom: 3 }}>
          {eyebrowText && (
            <Typography component="p" variant="textBold" sx={{ paddingBottom: 2 }}>
              {eyebrowText}
            </Typography>
          )}
          {title && (
            <Typography component="p" variant="textLargeBold">
              {title}
            </Typography>
          )}
          {description && (
            <Typography component="p" sx={{ marginTop: 1 }}>
              {description}
            </Typography>
          )}
        </Box>
      )}
      <Box component="ul" sx={{ padding: 0 }}>
        {geoTargetedCards.map((card, cardIndex) => (
          <ProductCardWrapper
            cardBadgeText={cardIndex === 0 ? cardBadgeText : undefined}
            key={cardIndex}
            card={card}
            cardIndex={cardIndex}
            productPositionBase={productPositionBase}
            isSeoBankingFFActive={isSeoBankingFFActive}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            isImproveQualityTrafficExperimentActive={isImproveQualityTrafficExperimentActive}
            shouldDisplayWealthFrontModal={shouldDisplayWealthFrontModal}
          />
        ))}
      </Box>
    </Box>
  );
}
